import { StandaloneChart, StandaloneChartSchema } from 'types'

import type { ChartPatchData } from './chartSerialization'
import { sanitizeInvertedYAxisRanges } from './chartSerialization'

/**
 * Local State layer for Process Data charts.
 *
 * A localStorage-backed snapshot of the chart's live state, written
 * continuously (debounced) by `ScatterplotProvider` and hydrated on mount with
 * precedence over the rolodex-saved chart config. The saved chart remains the
 * baseline: `useChartSave` keeps comparing live state against it, so the
 * unsaved-changes dot survives a browser refresh whenever local != saved.
 *
 * Format: the payload's `data` field is the exact snake_case `ChartPatchData` a
 * chart save writes (`buildChartPatchData`), so it parses back through the real
 * `StandaloneChartSchema` and hydrates through the same `applyChartConfig` path
 * a saved chart uses — no parallel serialization format to keep in step.
 * `view` carries the small amount of view-only state the saved entry does not
 * persist (auto-scale flag, exact custom axis ranges).
 */

export const CHART_LOCAL_STATE_VERSION = 1

const KEY_PREFIX = 'gecko.chartLocalState'

/** Storage scope for the unsaved ad-hoc chart page (`/process-data/view`). */
export const ADHOC_CHART_SCOPE = 'adhoc'

/** View-only state that a saved chart entry does not carry. */
export interface ChartLocalViewState {
  autoScaleMode?: boolean
  /**
   * The exact live axis ranges. `source_configs.y_axis_range` only stores them
   * while auto-scale is off, so this keeps the user's entered values across a
   * refresh regardless of the auto-scale toggle.
   */
  customAxisRanges?: Record<string, { min?: number; max?: number }>
}

export interface ChartLocalState {
  version: number
  updatedAt: string
  data: ChartPatchData
  view?: ChartLocalViewState
}

/**
 * Key scheme: `gecko.chartLocalState.v<version>.<siteID>.<scope>` where scope
 * is the chart id for saved charts, or `adhoc` for the ad-hoc page (whose
 * transient slug regenerates every mount and therefore cannot key anything).
 * The version lives in the key so a schema change orphans old entries instead
 * of tripping over them; the payload carries it too and is validated on read.
 */
export const chartLocalStateKey = (siteID: string, scope: string): string =>
  `${KEY_PREFIX}.v${CHART_LOCAL_STATE_VERSION}.${siteID}.${scope}`

/** Scope prefix for the workbench's per-workspace-tab draft keys. */
export const workspaceTabScopePrefix = (tabId: string): string =>
  `wstab-${tabId}-`

/** Drops every draft a closing workspace tab left behind, across sites. */
export const clearChartLocalStateForWorkspaceTab = (tabId: string): void => {
  const storage = getStorage()
  if (!storage) return
  const scopeMarker = `.${workspaceTabScopePrefix(tabId)}`
  const doomed: string[] = []
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i)
    if (key?.startsWith(KEY_PREFIX) && key.includes(scopeMarker)) {
      doomed.push(key)
    }
  }
  doomed.forEach((key) => storage.removeItem(key))
}

/** Drops a deleted chart's drafts, including per-workspace-tab copies. */
export const clearChartLocalStateForChart = (
  siteID: string,
  chartID: string
): void => {
  const storage = getStorage()
  if (!storage) return
  const sitePrefix = `${KEY_PREFIX}.v${CHART_LOCAL_STATE_VERSION}.${siteID}.`
  const doomed: string[] = []
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i)
    if (!key?.startsWith(sitePrefix)) continue
    const scope = key.slice(sitePrefix.length)
    if (scope === chartID || scope.endsWith(`-${chartID}`)) {
      doomed.push(key)
    }
  }
  doomed.forEach((key) => storage.removeItem(key))
}

const getStorage = (): Storage | null => {
  try {
    return window.localStorage
  } catch {
    // Storage can throw outright (sandboxed iframes, disabled cookies).
    return null
  }
}

/**
 * Read and validate local state. Anything malformed — unparseable JSON, a
 * version mismatch, a payload without the expected shape — is discarded (and
 * removed) rather than surfaced.
 */
export const readChartLocalState = (key: string): ChartLocalState | null => {
  const storage = getStorage()
  if (!storage) return null

  let raw: string | null = null
  try {
    raw = storage.getItem(key)
  } catch {
    return null
  }
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as ChartLocalState
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      parsed.version !== CHART_LOCAL_STATE_VERSION ||
      !parsed.data ||
      typeof parsed.data !== 'object'
    ) {
      storage.removeItem(key)
      return null
    }
    return parsed
  } catch {
    try {
      storage.removeItem(key)
    } catch {
      // Nothing else to do.
    }
    return null
  }
}

export const writeChartLocalState = (
  key: string,
  data: ChartPatchData,
  view?: ChartLocalViewState
): void => {
  const storage = getStorage()
  if (!storage) return

  const state: ChartLocalState = {
    version: CHART_LOCAL_STATE_VERSION,
    updatedAt: new Date().toISOString(),
    data,
    ...(view && { view }),
  }
  try {
    storage.setItem(key, JSON.stringify(state))
  } catch {
    // Quota exceeded / private mode — local state is best-effort.
  }
}

export const clearChartLocalState = (key: string): void => {
  const storage = getStorage()
  if (!storage) return
  try {
    storage.removeItem(key)
  } catch {
    // Best-effort.
  }
}

/**
 * Move local state from one key to another — used when an ad-hoc chart is
 * saved and promoted to a real chart id, so its state follows it.
 */
export const migrateChartLocalState = (
  fromKey: string,
  toKey: string
): void => {
  if (fromKey === toKey) return
  const state = readChartLocalState(fromKey)
  if (state) {
    writeChartLocalState(toKey, state.data, state.view)
  }
  clearChartLocalState(fromKey)
}

/**
 * Parse the stored payload back into a `StandaloneChart` through the real
 * schema, so hydration reuses `applyChartConfig` exactly like a saved chart.
 * Returns null (never throws) when the payload no longer parses.
 *
 * Inverted y-axis ranges (min > max, which the schema refuses) are sanitized
 * away first: one bad pen bound written by an older build must cost only that
 * bound, never the whole stored state.
 */
export const parseChartLocalState = (
  state: ChartLocalState
): StandaloneChart | null => {
  const parsed = StandaloneChartSchema.safeParse(
    sanitizeInvertedYAxisRanges(state.data)
  )
  return parsed.success ? parsed.data : null
}
